import { ISupplierReference, permanentFormIds } from "../../../models"
import { ITechRawData } from "../../../models/bo"
import combineReducers, {
  Action,
  StateFromReducersMapObject,
} from "../../../utils/combine-reducer"
import { IProductFormActionTypesAndValues } from "../_actions"
import ICreateField, {
  FieldDataType,
  FormFieldTypes,
  IFieldState,
} from "./_utils/product-form-types"
import createField from "./_utils/create-field"

export interface ITechFields {
  [x: string]: IFieldState<any>
}

const isInitialized = (
  state: boolean = false,
  action: Action<IProductFormActionTypesAndValues>
): boolean => {
  if (action.formId === permanentFormIds.NEW_PRODUCT) {
    return true
  }

  switch (action.type) {
    case "STORE_PRODUCT":
      return true
    case "REFETCH_PRODUCT_DATA":
      return false
    default:
      return state
  }
}

const product = (
  state: ISupplierReference | null = null,
  action: Action<IProductFormActionTypesAndValues>
): ISupplierReference | null => {
  switch (action.type) {
    case "STORE_PRODUCT":
      return action.value.product
    default:
      return state
  }
}

export interface IPreviousVersions {
  [date: string]: ISupplierReference | "loading"
}
const previousVersions = (
  state: IPreviousVersions = {},
  action: Action<IProductFormActionTypesAndValues>
): IPreviousVersions => {
  switch (action.type) {
    case "STORE_PRODUCT":
      const newState = action.value.product.previousVersionDates
        ? action.value.product.previousVersionDates.reduce(
            (acc, d) => {
              if (acc[d] && acc[d] !== "loading") {
                return acc
              } else {
                return { ...acc, [d]: "loading" } as IPreviousVersions
              }
            },
            { ...state } as IPreviousVersions
          )
        : state
      return newState
    case "STORE_PREVIOUS_VERSION":
      return { ...state, [action.value.date]: action.value.previousVersion }
    default:
      return state
  }
}

const selectedPreviousVersion = (
  state: string | null = null,
  action: Action<IProductFormActionTypesAndValues>
): string | null => {
  switch (action.type) {
    case "STORE_PRODUCT":
      return (
        state ||
        action.value.product.previousVersionDates?.[
          action.value.product.previousVersionDates?.length - 1 || 0
        ] ||
        null
      )
    case "SELECT_PREVIOUS_VERSION":
      return action.value
    default:
      return state
  }
}

const techFieldsRawData = (
  state: ITechRawData[] = [],
  action: Action<IProductFormActionTypesAndValues>
): ITechRawData[] => {
  switch (action.type) {
    case "STORE_TECH_FIELDS":
      return action.value.rawData
    default:
      return state
  }
}

const deletedFileNames = (
  state: string[] = [],
  action: Action<IProductFormActionTypesAndValues>
): string[] => {
  switch (action.type) {
    case "DELETE_FILE":
      if (!action.value.fileInfos.isNew) {
        return [...state, action.value.fileInfos.file.name]
      }
      return state

    case "STORE_PRODUCT":
    case "RESET_FORM":
      return []

    default:
      return state
  }
}

const techFields = (
  state: ITechFields = {},
  action: Action<IProductFormActionTypesAndValues>
): ITechFields => {
  switch (action.type) {
    case "STORE_TECH_FIELDS":
      return action.value.fields.reduce(
        (accumulator: ITechFields, caratechFieldInfos: ICreateField<any>) => {
          const fieldInfos: ICreateField<any> = {
            ...caratechFieldInfos,
            localized: caratechFieldInfos.isDeclinable
              ? false
              : caratechFieldInfos.localized,
          }
          return {
            ...accumulator,
            [caratechFieldInfos.code]: createField(fieldInfos)(
              state[caratechFieldInfos.code],
              action
            ),
          }
        },
        {}
      )
    default:
      return Object.keys(state).length > 0
        ? Object.values(state).reduce(
            (accumulator: ITechFields, caratechField) => {
              return {
                ...accumulator,
                [caratechField.code]: createField(
                  caratechField.regenerateInfos
                )(state[caratechField.code], action),
              }
            },
            {} as ITechFields
          )
        : state
  }
}

const standardFieldsReducers = {
  comTreeState: createField({
    label: "comTreeState",
    code: "comTreeState",
    dataType: FieldDataType.TreeState,
  }),
  techTreeState: createField({
    label: "techTreeState",
    code: "techTreeState",
    dataType: FieldDataType.TreeState,
  }),
  comNode: createField({
    label: "comNode",
    code: "comNode",
    dataType: FieldDataType.Node,
    isRequired: true,
  }),
  techNode: createField({
    label: "techNode",
    code: "techNode",
    dataType: FieldDataType.Node,
    isRequired: true,
  }),
  ean: createField({
    label: "ean",
    code: "ean",
    dataType: FieldDataType.Text,
    localized: false,
    formFieldType: FormFieldTypes.Input,
    isRequired: true,
  }),
  label: createField({
    label: "label",
    code: "label",
    dataType: FieldDataType.MultiLang,
    localized: true,
    formFieldType: FormFieldTypes.Input,
    isRequired: true,
  }),
  pictures: createField({
    label: "pictures",
    code: "pictures",
    dataType: FieldDataType.FileList,
    defaultValue: [],
    isRequired: true,
  }),
  pdf: createField({
    label: "pdf",
    code: "pdf",
    dataType: FieldDataType.FileList,
    defaultValue: [],
  }),
  price: createField({
    label: "price",
    code: "price",
    dataType: FieldDataType.Number,
    defaultValue: 0,
    formFieldType: FormFieldTypes.Number,
    isRequired: true,
  }),
  manufacturerRef: createField({
    label: "manufacturerRef",
    code: "manufacturerRef",
    dataType: FieldDataType.Text,
    localized: false,
    formFieldType: FormFieldTypes.Input,
    isRequired: false,
  }),
  supplierRef: createField({
    label: "supplierRef",
    code: "supplierRef",
    dataType: FieldDataType.Text,
    localized: false,
    formFieldType: FormFieldTypes.Input,
    isRequired: true,
  }),
  droopyId: createField({
    label: "droopyId",
    code: "droopyId",
    dataType: FieldDataType.Text,
  }),
  buyingReasons: createField({
    label: "buyingReasons",
    code: "buyingReasons",
    dataType: FieldDataType.MultiLang,
    localized: true,
    formFieldType: FormFieldTypes.Textarea,
    isRequired: true,
  }),
  purchaseLabel: createField({
    label: "purchaseLabel",
    code: "purchaseLabel",
    dataType: FieldDataType.MultiLang,
    localized: true,
    formFieldType: FormFieldTypes.Textarea,
    isRequired: true,
  }),
  purchaseIntroduction: createField({
    label: "purchaseIntroduction",
    code: "purchaseIntroduction",
    dataType: FieldDataType.MultiLang,
    formFieldType: FormFieldTypes.Textarea,
    localized: true,
  }),
  activated: createField({
    label: "activated",
    code: "activated",
    dataType: FieldDataType.Boolean,
    formFieldType: FormFieldTypes.Checkbox,
    defaultValue: true,
  }),
  validated: createField({
    label: "validated",
    code: "validated",
    dataType: FieldDataType.Boolean,
    defaultValue: false,
  }),
  isTopRef: createField({
    label: "isTopRef",
    code: "isTopRef",
    dataType: FieldDataType.Boolean,
    formFieldType: FormFieldTypes.Checkbox,
    defaultValue: false,
  }),
  declination: createField({
    label: "declination",
    code: "declination",
    dataType: FieldDataType.Dropdown,
    defaultValue: "-1",
    formFieldType: FormFieldTypes.Dropdown,
    isRequired: true,
  }),
  declined: createField({
    label: "declined",
    code: "declined",
    dataType: FieldDataType.Dropdown,
    defaultValue: [],
    formFieldType: FormFieldTypes.Dropdown,
    isRequired: true,
  }),
  declinedToAdd: createField({
    label: "declinedToAdd",
    code: "declinedToAdd",
    dataType: FieldDataType.Dropdown,
    defaultValue: [],
    formFieldType: FormFieldTypes.Dropdown,
    isRequired: false,
  }),
  topRefId: createField({
    label: "topRefId",
    code: "topRefId",
    dataType: FieldDataType.Number,
  }),
  customsCode: createField({
    label: "customsCode",
    code: "customsCode",
    dataType: FieldDataType.Text,
    formFieldType: FormFieldTypes.Search,
    isRequired: true,
  }),
  vatRate: createField({
    label: "vatRate",
    code: "vatRate",
    dataType: FieldDataType.Dropdown,
    defaultValue: "-1",
    formFieldType: FormFieldTypes.Dropdown,
    isRequired: true,
  }),
  supplierWarranty: createField({
    label: "supplierWarranty",
    code: "supplierWarranty",
    dataType: FieldDataType.Text,
    defaultValue: "",
    formFieldType: FormFieldTypes.Input,
    isRequired: false,
  }),
  bruneauWarranty: createField({
    label: "bruneauWarranty",
    code: "bruneauWarranty",
    dataType: FieldDataType.Dropdown,
    defaultValue: "-1",
    formFieldType: FormFieldTypes.Dropdown,
    isRequired: false,
  }),
  selectedTaxes: createField({
    label: "selectedTaxes",
    code: "selectedTaxes",
    dataType: FieldDataType.Dropdown,
    defaultValue: [],
    formFieldType: FormFieldTypes.Dropdown,
    isRequired: false,
  }),
  taxValues: createField({
    label: "taxValues",
    code: "taxValues",
    dataType: FieldDataType.Tax,
    defaultValue: [],
    formFieldType: FormFieldTypes.Custom,
    isRequired: false,
  }),
  originCountry: createField({
    label: "originCountry",
    code: "originCountry",
    dataType: FieldDataType.Dropdown,
    defaultValue: "-1",
    formFieldType: FormFieldTypes.Dropdown,
    isRequired: true,
  }),
  brand: createField({
    label: "brand",
    code: "brand",
    dataType: FieldDataType.Dropdown,
    defaultValue: "-1",
    formFieldType: FormFieldTypes.Dropdown,
    isRequired: false,
  }),
  minOrder: createField({
    label: "minOrder",
    code: "minOrder",
    dataType: FieldDataType.Number,
    formFieldType: FormFieldTypes.Number,
    defaultValue: 1,
    isRequired: true,
  }),
}

export interface IFields {
  [x: string]: IFieldState<any>
}
export type IStandardFields = StateFromReducersMapObject<
  typeof standardFieldsReducers
>
const standardFields = combineReducers<typeof standardFieldsReducers>(
  standardFieldsReducers
)
const formIdReducers = {
  isInitialized,
  product,
  previousVersions,
  selectedPreviousVersion,
  techFields,
  standardFields,
  techFieldsRawData,
  deletedFileNames,
}

export type formIdReducers = typeof formIdReducers
const formIdReducer = combineReducers<typeof formIdReducers>({
  ...formIdReducers,
})

export default formIdReducer
